import Dialog, { type DialogProps } from "@mui/material/Dialog";
import styled from "styled-components";
import { useSwipeDismiss } from "../hooks/useSwipeDismiss";

// styled-components applies its class to MUI's Paper slot, which is what
// actually needs the styling (Dialog itself is just the backdrop+positioning
// machinery).
//
// Solid --surface-opaque, not the translucent --surface: a blurred
// translucent Paper sits above MUI's default Backdrop scrim
// (rgba(0,0,0,.5)), so backdrop-filter samples that darkened scrim through
// the translucency and washes every dialog out grey instead of the app's
// true white/dark surface color.
const GlassPaper = styled.div`
  background: var(--surface-opaque);
  border: 1px solid var(--surface-border);
  box-shadow:
    var(--shadow),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  border-radius: var(--radius);
  transition: var(--transition);

  @media (max-width: 768px) {
    border-radius: 14px;
  }

  // Fullscreen (mobile by default, see AppDialog's own fullScreen prop
  // below, or an explicit fullScreen override) should read as one
  // continuous page, not a floating card pinned edge-to-edge.
  //
  // The margin/width/height/maxWidth/maxHeight below are NOT redundant
  // with MUI's own fullScreen behavior - they're the actual fix for a real
  // bug. MUI's Dialog only applies its own margin:0/width:100%/height:100%
  // fullScreen sizing as part of ITS internal DialogPaper styled-component
  // (see the useSlot('paper', ...) call in MUI's own Dialog.js source).
  // Passing a custom paper slot component (GlassPaper, below) swaps out
  // that entire component, not just adds classes on top of it - the
  // paperFullScreen className still gets applied to GlassPaper, but none
  // of MUI's own CSS for that class ever gets generated, since
  // DialogPaper itself is never instantiated. Without this, every
  // fullScreen AppDialog silently renders as a shrink-to-fit box centered
  // in the viewport (the surrounding flex container centers rather than
  // stretches by default) instead of true edge-to-edge fullscreen -
  // invisible for most dialogs (their content just looks like a normally
  // sized dialog), but glaringly obvious for anything with an intentional
  // full-bleed background (confirmed via a real screenshot on the Sign In
  // screen: a visibly bounded rectangle floating on the backdrop instead
  // of covering the viewport).
  &.MuiDialog-paperFullScreen {
    border-radius: 0;
    border: none;
    box-shadow: none;
    margin: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: none;
  }
`;

// Shared wrapper for every MUI Dialog added during the migration.
//
// container is pinned to #react-root deliberately: MUI Dialogs portal to
// document.body by default, which is *outside* AppRoot's ScopedCssBaseline
// subtree - pinning it here keeps every dialog inside the scoped reset
// instead of repeating this on every call site.
//
// Also wires up useSwipeDismiss on the Paper - swipeDismiss.ts's generic
// bootstrap.Modal-based swipe-to-dismiss silently stops covering any
// dialog once it's no longer a bootstrap.Modal, so every AppDialog gets
// its own copy of that gesture instead.
//
// Always fullScreen, on every viewport - product decision: no floating
// "modal card" surfaces anywhere in this app, every dialog is a full
// dedicated page (see AppDialogHeader.tsx for the matching back-arrow
// header convention every dialog uses instead of an X-to-close). Still
// destructured out of `...props` so a call site could opt out by passing
// its own `fullScreen={false}` explicitly, though nothing in the app
// should do that.
export function AppDialog({
  slotProps,
  onClose,
  fullScreen,
  ...props
}: DialogProps) {
  const swipe = useSwipeDismiss(() => onClose?.({}, "backdropClick"));

  return (
    <Dialog
      container={() => document.getElementById("react-root")}
      onClose={onClose}
      fullScreen={fullScreen ?? true}
      slots={{ paper: GlassPaper }}
      slotProps={{
        ...slotProps,
        paper: {
          onTouchStart: swipe.onTouchStart,
          onTouchMove: swipe.onTouchMove,
          onTouchEnd: swipe.onTouchEnd,
          style: swipe.style,
          ...slotProps?.paper,
        },
      }}
      {...props}
    />
  );
}
